import { map, Observable } from 'rxjs';
import { inject, Pipe, PipeTransform } from '@angular/core';

/**
 * services
 */
import { ILayoutLang, LangService, LayoutLang } from '@core/services';

/**
 * utilities
 */
import { INTL_LOCALES, toArabicNumerals, toEnglishNumerals } from '@core/utilities';

/**
 * Pipe that renders a value in the numerals of the current language.
 *
 * **Logic:** Subscribes to `LangService.changeLang$`, which emits the active language
 * whenever the user switches language. Each emission is mapped to the corresponding
 * numeral format (Arabic ٠١٢٣٤٥٦٧٨٩ or English 0123456789).
 *
 * **Two paths, chosen by the caller.** Passing `fractionDigits` formats the value as a number
 * through `Intl.NumberFormat` on the language's {@link INTL_LOCALES} entry — grouping,
 * decimals and numerals all decided by the locale, the same mechanism `LocalizedCurrencyPipe`
 * uses, so money and readings in the same table agree. Omitting it transliterates the digits
 * and leaves everything else alone, which is what the majority of call sites need: they hand
 * over a value that already has its shape — a formatted date, a grid account number, a
 * calendar day label, a year — where grouping would be wrong and `Number()` would yield `NaN`.
 *
 * **LangService integration:** Uses the `changeLang$` Observable (derived from layout config).
 * The pipe is **pure** (default Angular behaviour). It exposes an `Observable` and is intended
 * to be used together with the `async` pipe, so the template automatically reacts to language
 * changes when `changeLang$` emits new values.
 */
@Pipe({
  name: 'localizedNumber',
})
export class LocalizedNumberPipe implements PipeTransform {

  /** LangService for current language. */
  public readonly _langService = inject(LangService);

  /**
   * Transforms value into the current language's numerals.
   * Subscribes to `changeLang$` and maps each emitted lang to the correct numeral format.
   *
   * @param value - Number or string to transform. `null`/`undefined` yield an
   * empty string.
   * @param fractionDigits - Number of decimals — the most, when `minFractionDigits` is
   * smaller. Supplying it formats `value` as a number for the active locale; omitting it
   * transliterates the digits it already has.
   * @param minFractionDigits - Fewest decimals, so trailing zeros up to `fractionDigits` are
   * dropped (`2 : 0` gives `100`, `27.5`, `50.81`). Defaults to `fractionDigits`: a fixed count.
   * @returns Observable that emits the localized value whenever the language changes.
   *
   * @remarks
   * Nullish input is accepted rather than rejected because the pipe's natural
   * position is at the end of a chain — `date | date: 'dd/MM/yyyy' | localizedNumber` —
   * and Angular's own `DatePipe` is typed as returning `string | null`. Throwing
   * there would make the common case a type error at every call site, so an
   * absent value renders as nothing instead.
   */
  public transform(
    value: number | string | null | undefined,
    fractionDigits?: number,
    minFractionDigits?: number,
  ): Observable<string> {
    return this._langService.changeLang$.pipe(
      map(lang => formatLocalizedNumber(value, lang, fractionDigits, minFractionDigits)),
    );
  }

}

/**
 * Renders a value in one language's numerals, as {@link LocalizedNumberPipe} does.
 *
 * @param value - Number or string to render. `null`/`undefined` and empty yield an empty
 * string; with `fractionDigits`, so does a non-numeric value.
 * @param lang - Language whose numerals and {@link INTL_LOCALES} entry are used.
 * @param fractionDigits - Number of decimals — the most, when `minFractionDigits` is smaller.
 * Omitted, only the value's digits are transliterated.
 * @param minFractionDigits - Fewest decimals; defaults to `fractionDigits`. Never more than it.
 * @returns The localized text.
 *
 * @remarks
 * The body of {@link LocalizedNumberPipe}, lifted out so a caller with no template — a file
 * export, say — renders a number exactly as the screen does, as `formatLocalizedCurrency` does
 * for money. Templates still use the pipe.
 */
export function formatLocalizedNumber(
  value: number | string | null | undefined,
  lang: ILayoutLang,
  fractionDigits?: number,
  minFractionDigits: number | undefined = fractionDigits,
): string {
  if (value == null || value === '') return '';

  // No digit count asked for: the value already carries its own shape — a formatted
  // date, an account number, a day label, a year — so only its glyphs are swapped.
  if (fractionDigits == null) {
    return lang === LayoutLang.Ar ? toArabicNumerals(value) : toEnglishNumerals(value);
  }

  const amount = Number(value);

  if (!Number.isFinite(amount)) return '';

  return new Intl.NumberFormat(INTL_LOCALES[lang] ?? INTL_LOCALES[LayoutLang.En], {
    // Intl rejects a minimum above the maximum
    minimumFractionDigits: Math.min(minFractionDigits ?? fractionDigits, fractionDigits),
    maximumFractionDigits: fractionDigits,
  }).format(amount);
}
